import { useReducer } from "react";

import { Callout, Icon, Spinner } from "@blueprintjs/core";
import { useNavigate } from "react-router";

import callend from "@/../../hypetrain/static/img/exec/white/icon-callend.svg";
import { removeItem } from "@/components/helpers/storage/local-storage-utils";
import { convertHtmlToMarkdown } from "@/components/helpers/string-utils";
import { CreateScenarioInput } from "@/components/pages_logged_in/roleplays/details/create/CreateScenarioInput";
import { ScenarioHeader } from "@/components/pages_logged_in/roleplays/details/ScenarioHeader";
import { buildIntakeUrl } from "@/components/pages_logged_in/roleplays/utils";
import LiveCallManager, {
  CallState,
  CallStatus,
} from "@/components/pieces/roleplays/LiveCallManager";
import { formatTime } from "@/components/pieces/roleplays/utils";
import { Button } from "@/components/ui/button";
import "@/styles/global.css";

import { VolumeVisualizer } from "@/components/ui/volume-visualizer";
import { STANDARD_NAV_HEIGHT } from "@/css/constants";
import {
  RoleplayQuery,
  useCreateScenarioIntakeMutation,
  useQuickCreateScenarioIntakeMutation,
} from "@/graphql";
import { formatErrors } from "@/utils/array";

const setString = (key: string, value: string) => {
  window.localStorage.setItem(key, value);
};

interface ScenarioCreateState {
  scenarioPrompt: string;
  state: "manual" | "voice";
  level?: number;
  error?: string;
}

interface StringScenarioCreateAction {
  type: "setScenarioPrompt" | "setVoice" | "setManual" | "setError";
  payload?: string;
}

interface NumberScenarioCreateAction {
  type: "setLevel";
  payload?: number;
}

type ScenarioCreateAction =
  | StringScenarioCreateAction
  | NumberScenarioCreateAction;

interface VoiceControlsProps {
  callState: CallState;
  onRestartCall: () => void;
  onEndCall: () => void;
  onCancelCall: () => void;
}
function VoiceControls({
  callState,
  onRestartCall,
  onEndCall,
  onCancelCall,
}: VoiceControlsProps) {
  return (
    <div className="flex gap-2">
      {(callState.status === CallStatus.STARTING ||
        callState.status === CallStatus.RESTARTING) && (
        <Button intent="secondary" disabled className="w-40">
          {`Starting in ${callState.countDown}...`}
        </Button>
      )}
      {callState.status === CallStatus.CONNECTING && (
        <Button intent="secondary" disabled className="w-40">
          <Spinner size={16} />
          Connecting...
        </Button>
      )}
      {callState.status === CallStatus.CONNECTED && (
        <Button intent="success" onClick={onEndCall} className="w-40">
          <img src={callend} />
          End
        </Button>
      )}
      {callState.status === CallStatus.ENDING && (
        <Button intent="secondary" disabled className="w-40">
          <Spinner size={16} />
          Saving...
        </Button>
      )}
      {/* NOTE: the way we use LiveCallManager for this component means that it
      should never be in the IDLE state, so if it is, there is a problem */}
      {(callState.status === CallStatus.SAVE_FAILED ||
        callState.status === CallStatus.ERROR ||
        callState.status === CallStatus.IDLE) && (
        <Button intent="secondary" disabled className="w-40">
          Problem with call
        </Button>
      )}
      <Button
        intent="secondary"
        onClick={onRestartCall}
        disabled={callState.status !== CallStatus.CONNECTED}
      >
        <Icon icon="reset" />
      </Button>
      <Button intent="secondary" onClick={onCancelCall}>
        Cancel
      </Button>
    </div>
  );
}

function scenarioCreateReducer(
  state: ScenarioCreateState,
  action: ScenarioCreateAction,
): ScenarioCreateState {
  switch (action.type) {
    case "setScenarioPrompt":
      window.localStorage.setItem("scenarioPrompt", action.payload || "");
      return { ...state, scenarioPrompt: action.payload || "" };
    case "setVoice":
      return { ...state, state: "voice" };
    case "setManual":
      return { ...state, state: "manual" };
    case "setLevel":
      return { ...state, level: action.payload };
    case "setError":
      return { ...state, error: action.payload };
    default:
      return state;
  }
}

const INTAKE_ASSISTANT_ID = "57f27af6-62dd-4b04-8344-423918d57044";

export function ScenarioCreateContent({
  roleplay,
  loading = false,
}: {
  roleplay?: RoleplayQuery["roleplay"];
  loading?: boolean;
}) {
  const [state, dispatch] = useReducer(scenarioCreateReducer, {
    scenarioPrompt: window.localStorage.getItem("scenarioPrompt") || "",
    state: "manual",
  });

  const navigate = useNavigate();

  const [saveCall, { loading: saveCallLoading }] =
    useCreateScenarioIntakeMutation({
      onCompleted: (data) => {
        const intakeUuid = data?.createScenarioIntake?.scenarioIntake?.uuid;
        if (!intakeUuid) {
          dispatch({ type: "setError", payload: "Failed to create intake" });
          return;
        }
        navigate(buildIntakeUrl(intakeUuid));
      },
      onError: (error) => {
        dispatch({ type: "setError", payload: error.message });
      },
    });
  const [
    quickCreateScenarioIntake,
    { loading: quickCreateScenarioIntakeLoading },
  ] = useQuickCreateScenarioIntakeMutation({
    onCompleted: (data) => {
      if (data?.quickCreateScenarioIntake?.errors?.length) {
        dispatch({
          type: "setError",
          payload: formatErrors(data.quickCreateScenarioIntake.errors),
        });
        return;
      }
      const intakeUuid = data?.quickCreateScenarioIntake?.scenarioIntake?.uuid;
      if (!intakeUuid) {
        dispatch({ type: "setError", payload: "Failed to create intake" });
        return;
      }
      setString("scenarioPrompt", "");
      removeItem("scenarioPromptTemplateId");
      navigate(buildIntakeUrl(intakeUuid));
    },
    onError: (error) => {
      dispatch({ type: "setError", payload: error.message });
    },
  });
  return (
    <div
      className="md:ml-sidebar flex flex-col"
      style={{
        height: `calc(100vh - ${STANDARD_NAV_HEIGHT}px)`,
      }}
    >
      <div className="hidden md:block">
        <ScenarioHeader
          page={"scenarioCreate"}
          roleplay={roleplay}
          loading={loading}
          noMargins
          py={3}
        />
      </div>
      {state.error && (
        <div className="p-4">
          <Callout intent="warning">{state.error}</Callout>
        </div>
      )}
      <LiveCallManager
        assistantId={INTAKE_ASSISTANT_ID}
        handleSaveCall={(callId) => {
          if (callId) {
            saveCall({
              variables: {
                roleplayId: roleplay?.id,
                callId,
              },
            });
          }
        }}
        onVolumeChange={(level) =>
          dispatch({ type: "setLevel", payload: level })
        }
      >
        {(props) =>
          state.state === "manual" ? (
            <div className="flex min-h-0 flex-auto flex-col items-center gap-8 p-2 pt-8 md:p-4 md:pt-8">
              <div className="font-serif text-4xl font-medium">
                Describe the scenario
              </div>
              <CreateScenarioInput
                value={state.scenarioPrompt}
                loading={saveCallLoading || quickCreateScenarioIntakeLoading}
                onChange={(value) =>
                  dispatch({ type: "setScenarioPrompt", payload: value })
                }
                onCreate={({ content, selectedTemplate, attachedContexts }) => {
                  quickCreateScenarioIntake({
                    variables: {
                      roleplayId: roleplay?.id,
                      scenarioPrompt: convertHtmlToMarkdown(content),
                      templateId: selectedTemplate?.id,
                      workspaceContextIds: attachedContexts,
                    },
                  });
                  dispatch({ type: "setError", payload: undefined });
                }}
                onVoiceChat={() => {
                  dispatch({ type: "setVoice" });
                  props.onStartCall();
                }}
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-9 p-8">
              <div className="font-serif text-4xl font-medium">Let's chat</div>
              <VolumeVisualizer level={state.level} />
              <div className="flex flex-col items-center gap-2">
                <VoiceControls
                  callState={props.callState}
                  onRestartCall={() => {
                    dispatch({ type: "setVoice" });
                    props.onRestartCall();
                  }}
                  onEndCall={props.onEndCall}
                  onCancelCall={() => {
                    dispatch({ type: "setManual" });
                    props.onCancelCall();
                  }}
                />
                {props.callState.status === CallStatus.CONNECTED && (
                  <div>{formatTime(props.callDuration)}</div>
                )}
              </div>
            </div>
          )
        }
      </LiveCallManager>
    </div>
  );
}
